import { icon } from '@/assets';
import newsPng from '@/assets/img/news.png';
import { notification, NotificationArgsProps } from 'antd';
import React, { createContext, ReactNode, useContext, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

/**
 * 通知上下文类型接口定义
 * 包含了通知相关的所有方法类型
 */
interface NotificationContextType {
  /**
   * 显示通用通知的方法
   * @param config 通知配置参数，包含消息类型、内容等
   */
  showNotification: (config: NotificationArgsProps) => void;
  /**
   * 显示成功类型通知
   * @param message 通知的主要文本内容
   * @param description 通知的详细描述内容（可选）
   */
  showSuccess: (message: string, description?: string) => void;
  /**
   * 显示错误类型通知
   * @param message 通知的主要文本内容
   * @param description 通知的详细描述内容（可选）
   */
  showError: (message: string, description?: string) => void;
  /**
   * 显示信息类型通知
   * @param message 通知的主要文本内容
   * @param description 通知的详细描述内容（可选）
   */
  showInfo: (message: string, description?: string) => void;
  /**
   * 显示警告类型通知
   * @param message 通知的主要文本内容
   * @param description 通知的详细描述内容（可选）
   */
  showWarning: (message: string, description?: string) => void;
  showScoketTips: (message: string, description: string, url?: string) => void;
  destroyAll: () => void;
}

const NotificationContext = createContext<NotificationContextType | null>(null);

/**
 * 通知提供者组件的属性接口定义
 * 用于配置通知组件的显示方式和行为
 */
interface NotificationProviderProps {
  children: ReactNode; // 子组件，将被包裹在通知提供者中
  maxCount?: number; // 可选，同时显示的最大通知数量
  duration?: number; // 可选，通知显示的持续时间(毫秒)
  placement?: 'topLeft' | 'topRight' | 'bottomLeft' | 'bottomRight'; // 可选，通知显示的位置
}

const Layout = (title: string, content: string, key: string, cb: () => void) => {
  return (
    <>
      <div
        className="jf-notification-close"
        onClick={() => {
          notification.destroy(key);
        }}
        role="button"
        tabIndex={0}
      >
        <img src={icon.newsClose} className="jf-notification-close-img" />
      </div>
      <div className="jf-notification-layout" onClick={() => cb()} role="button">
        <div className="jf-notification-layout-left">
          <img src={newsPng} className="jf-notification-layout-left-img" />
        </div>
        <div className="jf-notification-layout-right">
          <div className="jf-notification-layout-right-title">{title}</div>
          <div className="jf-notification-layout-right-content">{content}</div>
        </div>
      </div>
    </>
  );
};

export const NotificationProvider: React.FC<NotificationProviderProps> = ({
  children,
  maxCount = 5,
  duration = 3,
  placement = 'topRight',
}) => {
  const [api, contextHolder] = notification.useNotification({
    maxCount,
    duration,
    placement,
    stack: false,
  });

  const navigate = useNavigate();

  const showNotification = (config: NotificationArgsProps) => {
    api.open({
      ...config,
    });
  };

  const showSuccess = (message: string, description?: string) => {
    api.success({
      message,
      description,
    });
  };

  const showError = (message: string, description?: string) => {
    api.error({
      message,
      description,
    });
  };

  const showInfo = (message: string, description?: string) => {
    api.info({
      message,
      description,
    });
  };

  const showWarning = (message: string, description?: string) => {
    api.warning({
      message,
      description,
    });
  };

  const showScoketTips = (message: string, description: string, url?: string) => {
    const key = Math.random().toString(36).substring(2, 8);
    api.open({
      className: 'jf-notification',
      message: Layout(message, description, key, () => {
        if (url) {
          navigate(url);
        }
      }),
      closeIcon: false,
      key,
      onClick: () => {
        api.destroy(key);
      },
    });
  };

  const destroyAll = () => {
    api.destroy();
  };

  const contextValue = useMemo<NotificationContextType>(
    () => ({
      showNotification,
      showSuccess,
      showError,
      showInfo,
      showWarning,
      destroyAll,
      showScoketTips,
    }),
    [],
  );

  return (
    <NotificationContext.Provider value={contextValue}>
      {contextHolder}
      {children}
    </NotificationContext.Provider>
  );
};

export const useNotification = (): NotificationContextType => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotification must be used within NotificationProvider');
  }
  return context;
};
